<template>
  <div class="h-full flex flex-col gap-y-4">
    <div class="flex items-center justify-between gap-x-3">
      <div>
        <div class="text-13 font-700">Tools</div>
        <div class="text-12 text-semi-dark-text">{{ isFixed ? 'Predefined tools provided by this adapter.' : 'Define safe named SQL tools for this adapter.' }}</div>
      </div>
      <PillButton v-if="!isFixed" text="New tool" primary @click="openToolModal()" />
    </div>

    <div v-if="!tools.length" class="text-center text-semi-dark-text text-13 py-10 border border-dashed border-stroke rounded-8">No tools yet</div>
    <div v-else class="flex flex-col gap-y-3 pb-4">
      <div v-for="tool in tools" :key="tool.id" class="border border-solid border-stroke rounded-8 bg-white overflow-hidden">
        <!-- Header row (always visible) -->
        <div class="flex items-start justify-between gap-x-4 p-4 cursor-pointer select-none hover:bg-gray-50" @click="toggleExpand(tool.id)">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-x-2 flex-wrap">
              <span class="font-700 truncate">{{ tool.name }}</span>
              <Badge :text="tool.enabled === false ? 'Disabled' : 'Enabled'" :gray="tool.enabled === false" :success="tool.enabled !== false" />
              <Badge v-if="tool.implementation && tool.implementation.method" :text="tool.implementation.method" gray small />
              <Badge v-if="tool.output && tool.output.type" :text="tool.output.type" info small />
            </div>
            <div class="text-12 text-semi-dark-text mt-1">{{ tool.description }}</div>
          </div>
          <div class="flex gap-x-2 flex-shrink-0 items-center" @click.stop>
            <PillButton v-if="!isFixed" text="Edit" @click="openToolModal(tool)" />
            <PillButton v-if="!isFixed" text="Delete" error @click="deleteTool(tool)" />
            <div
              class="w-6 h-6 flex items-center justify-center text-semi-dark-text transition-transform duration-200"
              :style="expandedToolIds[tool.id] ? 'transform: rotate(180deg)' : ''"
              @click.stop="toggleExpand(tool.id)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Expanded body -->
        <div v-if="expandedToolIds[tool.id]" class="border-t border-stroke">
          <div class="p-4 flex flex-col gap-y-4">
            <!-- Meta info row -->
            <div class="grid grid-cols-2 gap-2 text-12 sm:grid-cols-4">
              <div class="bg-on-tertiary rounded-6 px-3 py-2">
                <div class="text-semi-dark-text mb-0.5">Method</div>
                <div class="font-600">{{ (tool.implementation && tool.implementation.method) || '—' }}</div>
              </div>
              <div class="bg-on-tertiary rounded-6 px-3 py-2">
                <div class="text-semi-dark-text mb-0.5">Output type</div>
                <div class="font-600">{{ (tool.output && tool.output.type) || '—' }}</div>
              </div>
              <div class="bg-on-tertiary rounded-6 px-3 py-2">
                <div class="text-semi-dark-text mb-0.5">Created via</div>
                <div class="font-600">{{ tool.created_via || '—' }}</div>
              </div>
              <div class="bg-on-tertiary rounded-6 px-3 py-2">
                <div class="text-semi-dark-text mb-0.5">Last test</div>
                <div v-if="tool.test && tool.test.last_test_status" class="flex items-center gap-x-1">
                  <Badge
                    :text="tool.test.last_test_status"
                    :success="tool.test.last_test_status === 'success'"
                    :error="tool.test.last_test_status === 'error'"
                    :gray="tool.test.last_test_status !== 'success' && tool.test.last_test_status !== 'error'"
                    small
                  />
                </div>
                <div v-else class="font-600">—</div>
              </div>
            </div>

            <!-- Query block -->
            <div v-if="tool.implementation && tool.implementation.query">
              <div class="text-12 font-600 mb-1.5">Query</div>
              <pre class="text-11 overflow-auto bg-on-tertiary rounded-8 p-3">{{ tool.implementation.query }}</pre>
            </div>

            <!-- Parameters -->
            <div>
              <div class="text-12 font-600 mb-1.5">Input parameters</div>
              <div v-if="hasParameters(tool)" class="flex flex-col gap-y-1">
                <div v-for="(param, key) in tool.parameters.properties" :key="key" class="flex items-center gap-x-2 text-12 bg-on-tertiary rounded-6 px-3 py-1.5">
                  <span class="font-600">{{ key }}</span>
                  <span class="text-semi-dark-text">{{ param.type || 'any' }}</span>
                  <Badge v-if="isRequired(tool, key)" text="required" warn small />
                  <span v-if="param.description" class="text-semi-dark-text ml-auto truncate">{{ param.description }}</span>
                </div>
              </div>
              <div v-else class="text-12 text-semi-dark-text">No input parameters</div>
            </div>

            <!-- Output columns -->
            <div v-if="tool.output && tool.output.columns && tool.output.columns.length">
              <div class="text-12 font-600 mb-1.5">Output columns</div>
              <div class="flex flex-wrap gap-1">
                <span v-for="col in tool.output.columns" :key="col.name || col" class="text-11 bg-on-tertiary rounded-6 px-2 py-1 font-600">{{ col.name || col }}</span>
              </div>
            </div>

            <!-- Test section -->
            <div class="border-t border-stroke pt-3">
              <div class="flex items-center justify-between mb-2">
                <div class="text-12 font-600">Test</div>
                <div class="flex gap-x-2">
                  <PillButton v-if="testResults[tool.id] || visibleTestError(tool)" text="Clear" @click="clearTestResult(tool.id)" />
                  <PillButton text="Run test" :loading="testingToolId === tool.id" @click="testTool(tool)" />
                </div>
              </div>
              <div v-if="hasParameters(tool)" class="mb-3">
                <div class="text-12 text-semi-dark-text mb-2">Parameter values for test run</div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <FormInput
                    v-for="(param, key) in tool.parameters.properties"
                    :key="key"
                    :modelValue="(testArgs[tool.id] || {})[key] || ''"
                    :label="isRequired(tool, key) ? key + ' *' : key"
                    :placeholder="param.description || param.type || 'value'"
                    @update:modelValue="setTestArg(tool.id, key, $event)"
                  />
                </div>
              </div>
              <div v-if="tool.test && tool.test.last_test_at" class="text-11 text-semi-dark-text mb-2">
                Last run: {{ formatDate(tool.test.last_test_at) }}
                <span v-if="visibleTestError(tool)" class="text-red-600 ml-2">{{ tool.test.last_test_error }}</span>
              </div>
              <pre v-if="testResults[tool.id]" class="text-11 overflow-auto bg-on-tertiary rounded-8 p-3">{{ testResults[tool.id] }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { apiDeleteDataAdapterTool, apiPostDataAdapterTool, apiPostDataAdapterToolTest, apiPutDataAdapterTool } from '@/helpers/api';
import DataAdapterToolModal from './DataAdapterToolModal.vue';

export default {
  name: 'DataAdapterTools',
  props: {
    adapter: Object,
  },
  data() {
    return {
      saving: false,
      testingToolId: null,
      testResults: {},
      // Tools whose saved last-test error is hidden: after Clear, and while a new test runs.
      hiddenTestErrors: {},
      testArgs: {},
      expandedToolIds: {},
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    adapterType() {
      return this.$route.params.dataAdapter;
    },
    tools() {
      return this.adapter?.payload?.tools || [];
    },
    isFixed() {
      // Predefined (read-only) tool sets are seeded by the backend with
      // created_via 'fixed' — derive read-only state from the data, not the adapter name.
      return this.tools.length > 0 && this.tools.every((tool) => tool.created_via === 'fixed');
    },
  },
  methods: {
    ...mapActions(['fetchProjectDataAdapter', 'showToastMessage']),
    toggleExpand(toolId) {
      this.expandedToolIds = { ...this.expandedToolIds, [toolId]: !this.expandedToolIds[toolId] };
      if (!this.testArgs[toolId]) {
        this.testArgs[toolId] = {};
      }
    },
    setTestArg(toolId, key, value) {
      if (!this.testArgs[toolId]) {
        this.testArgs[toolId] = {};
      }
      this.testArgs[toolId] = { ...this.testArgs[toolId], [key]: value };
    },
    hasParameters(tool) {
      return tool.parameters && tool.parameters.properties && Object.keys(tool.parameters.properties).length > 0;
    },
    isRequired(tool, key) {
      return Array.isArray(tool.parameters && tool.parameters.required) && tool.parameters.required.includes(key);
    },
    /**
     * Clears a tool's test output: the last response and its saved last-test error.
     * @param {string} toolId Tool id.
     * @returns {void}
     */
    clearTestResult(toolId) {
      const next = { ...this.testResults };
      delete next[toolId];
      this.testResults = next;
      this.hiddenTestErrors = { ...this.hiddenTestErrors, [toolId]: true };
    },
    /**
     * @param {object} tool Tool definition.
     * @returns {boolean} Whether the tool's saved last-test error is shown (it is hidden after Clear and during a new run).
     */
    visibleTestError(tool) {
      return Boolean(tool.test?.last_test_error) && !this.hiddenTestErrors[tool.id];
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleString();
    },
    openToolModal(tool) {
      let modal = null;
      modal = this.$showModal(
        DataAdapterToolModal,
        { editTool: tool, connectionType: this.adapter?.payload?.connection?.type },
        {
          save: async (payload) => {
            await this.saveTool(payload);
            modal.close();
          },
        },
      );
    },
    async saveTool(tool) {
      this.saving = true;
      const payload = { project_id: this.projectId, adapter_type: this.adapterType, ...tool };
      const response = tool.tool_id ? await apiPutDataAdapterTool(payload) : await apiPostDataAdapterTool(payload);
      this.saving = false;
      if (response.status === 200 && response.data?.success) {
        await this.fetchProjectDataAdapter({ projectId: this.projectId, adapterType: this.adapterType });
        // Saved, but the database could not describe the query's columns (e.g. a SQL error or no connection).
        const warning = response.data.output_warning;
        this.showToastMessage({ message: warning ? `Tool saved. ${warning}` : 'Tool saved', type: warning ? 'default' : 'success' });
      } else {
        this.showToastMessage({ message: response.data?.message || 'Failed to save tool', type: 'error' });
      }
    },
    async deleteTool(tool) {
      const response = await apiDeleteDataAdapterTool({ project_id: this.projectId, adapter_type: this.adapterType, tool_id: tool.id });
      if (response.status === 200 && response.data?.success) {
        await this.fetchProjectDataAdapter({ projectId: this.projectId, adapterType: this.adapterType });
        this.showToastMessage({ message: 'Tool deleted', type: 'success' });
      } else {
        this.showToastMessage({ message: response.data?.message || 'Failed to delete tool', type: 'error' });
      }
    },
    async testTool(tool) {
      // The previous outcome goes away as soon as the new run starts.
      this.clearTestResult(tool.id);
      this.testingToolId = tool.id;
      const args = this.hasParameters(tool) ? this.testArgs[tool.id] || {} : tool.test?.sample_args || {};
      const response = await apiPostDataAdapterToolTest({ project_id: this.projectId, adapter_type: this.adapterType, tool_id: tool.id, args });
      this.testingToolId = null;
      this.testResults = { ...this.testResults, [tool.id]: JSON.stringify(response.data, null, 2) };
      await this.fetchProjectDataAdapter({ projectId: this.projectId, adapterType: this.adapterType });
      // Show this run's saved error, if it failed.
      this.hiddenTestErrors = { ...this.hiddenTestErrors, [tool.id]: false };
    },
  },
};
</script>
